import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import {
  Box,
  Grid,
  TextField,
  Typography,
  CircularProgress
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import { useTranslation } from "react-i18next";

import { DEVICE_TYPE, useDevice } from "../../../../domain/device";
import { useApi } from "../../../../api";
import { useAuth, PERMISSIONS } from "../../../../auth";
import useI18nSnackbar from "../../../../hooks/useI18nSnackbar";
import Section from "../../../../elements/Section";
import SectionHeader from "../../../../elements/SectionHeader";
import SectionSubTitle from "../../../../elements/SectionSubTitle";
import I18nText from "../../../../elements/I18nText";
import { PrimaryButton } from "../../../../elements/Button";
import { toCamel } from "../../../../utils/stringUtils";

const ALARM_GROUPS = [
  {
    titleKey: "devices.settings.alarmTemplates.groups.general",
    types: [
      { key: "temperature" },
      { key: "power_failure" },
      { key: "generic" },
      {
        key: "custom",
        iotOnly: true,
        labelKey: "devices.settings.alarmTemplates.customIotLabel"
      }
    ]
  },
  {
    titleKey: "devices.settings.alarmTemplates.groups.errorCodes",
    types: [
      { key: "e002", isCode: true },
      { key: "e008", isCode: true },
      { key: "e009", isCode: true },
      { key: "e012", isCode: true, hiddenForIot: true },
      {
        key: "other_error_code",
        labelKey: "devices.settings.alarmTemplates.otherErrorCodeLabel"
      }
    ]
  }
];

const AlarmTemplatesContainer = () => {
  const { device } = useDevice();
  const api = useApi();
  const { t } = useTranslation();
  const { enqueueSnackbar } = useI18nSnackbar();
  const { hasPermission } = useAuth();
  const [templates, setTemplates] = useState(null);

  const canEdit = hasPermission(device.permissions, PERMISSIONS.CONFIGURATION);
  const isIotDevice = device.type === DEVICE_TYPE.IOT;

  const visibleGroups = ALARM_GROUPS.map(group => ({
    ...group,
    types: group.types.filter(type => {
      if (type.iotOnly && !isIotDevice) return false;
      return !(type.hiddenForIot && isIotDevice);
    })
  })).filter(group => group.types.length > 0);

  useEffect(() => {
    api
      .get(`/v2/devices/${device.id}/alarm-templates`)
      .then(response => setTemplates(response.data))
      .catch(() => setTemplates({}));
  }, [api, device]);

  const getLabel = type => {
    if (type.labelKey) {
      return t(type.labelKey);
    }
    if (type.isCode) {
      return `${t(`alarms.types.${type.key}.code`)} — ${t(
        `alarms.types.${type.key}.title`
      )}`;
    }
    return t(`alarms.types.${type.key}`);
  };

  const getEntry = key => (templates && templates[toCamel(key)]) || {};

  const buildInitialValues = () =>
    visibleGroups.reduce((acc, group) => {
      group.types.forEach(type => {
        const entry = getEntry(type.key);
        acc[type.key] = entry.custom || entry.default || "";
      });
      return acc;
    }, {});

  const handleSubmit = (values, { setSubmitting }) => {
    const params = {};
    Object.entries(values).forEach(([key, value]) => {
      const trimmed = value.trim();
      const defaultMessage = (getEntry(key).default || "").trim();
      // Unedited (still matches the standard message) or cleared fields fall
      // back to the backend's default instead of being stored as overrides.
      if (trimmed && trimmed !== defaultMessage) {
        params[key] = value;
      }
    });

    api
      .put(`/v2/devices/${device.id}/alarm-templates`, params)
      .then(() => {
        enqueueSnackbar("devices.settings.alarmTemplates.responses.updated", {
          variant: "success"
        });
        setSubmitting(false);
      })
      .catch(() => setSubmitting(false));
  };

  if (templates === null) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Section>
      <SectionHeader>
        <I18nText>devices.settings.alarmTemplates.title</I18nText>
      </SectionHeader>
      <Box mb={3}>
        <Typography variant="body2" color="textSecondary">
          <I18nText>devices.settings.alarmTemplates.description</I18nText>
        </Typography>
      </Box>
      <Formik
        initialValues={buildInitialValues()}
        enableReinitialize
        onSubmit={handleSubmit}
      >
        {({ handleSubmit, handleChange, handleBlur, values, isSubmitting }) => (
          <form onSubmit={handleSubmit}>
            {visibleGroups.map(group => (
              <Box mb={3} key={group.titleKey}>
                <SectionSubTitle>
                  <I18nText>{group.titleKey}</I18nText>
                </SectionSubTitle>
                <Grid container spacing={2}>
                  {group.types.map(type => (
                    <Grid item xs={12} sm={6} key={type.key}>
                      <TextField
                        name={type.key}
                        label={getLabel(type)}
                        value={values[type.key]}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        multiline
                        minRows={2}
                        variant="outlined"
                        size="small"
                        fullWidth
                        disabled={!canEdit || isSubmitting}
                        placeholder={t(
                          "devices.settings.alarmTemplates.placeholder"
                        )}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}
            {canEdit && (
              <Grid container>
                <Grid item xs={12} style={{ textAlign: "right" }}>
                  <PrimaryButton
                    type="submit"
                    disabled={isSubmitting}
                    icon={<SaveIcon />}
                  >
                    <I18nText>actions.save</I18nText>
                  </PrimaryButton>
                </Grid>
              </Grid>
            )}
          </form>
        )}
      </Formik>
    </Section>
  );
};

export default AlarmTemplatesContainer;
